import type { ReactNode } from 'react';
import { Icon, type IconName } from '../../ui/Icon';
import { useT } from '../../i18n/LangProvider';
import type { Lesson, LessonType } from '../../api/types';

/** Uploaded files come back as server-relative paths; external URLs pass through. */
export const fileUrl = (src: string) =>
  (/^(https?:)?\/\//.test(src) || src.startsWith('data:')
    ? src
    : `${import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'}${src}`);

export const COURSE_COLORS = ['orange', 'magenta', 'purple', 'info', 'ok', 'warn', 'neutral'] as const;
export const COURSE_ICONS: IconName[] = ['cap', 'book', 'shield', 'lock', 'tools', 'chart', 'users', 'bolt', 'star', 'map'];

export const LESSON_TYPES: { id: LessonType; label: string; icon: IconName }[] = [
  { id: 'article', label: 'Articolo', icon: 'book' },
  { id: 'video', label: 'Video', icon: 'play' },
  { id: 'document', label: 'Documento (PDF)', icon: 'file' },
  { id: 'link', label: 'Link esterno', icon: 'external' },
  { id: 'quiz', label: 'Quiz', icon: 'help' },
];

export const lessonMeta = (t: LessonType) => LESSON_TYPES.find((x) => x.id === t) ?? LESSON_TYPES[0];

/** Total minutes declared across a course's lessons. */
export const courseMinutes = (lessons: Lesson[]) =>
  lessons.reduce((sum, l) => sum + (l.durationMin ?? 0), 0);

export function LessonBadge({ type }: { type: LessonType }) {
  const t = useT();
  const m = lessonMeta(type);
  return <span className={`lrn-ltype is-${type}`}><Icon name={m.icon} size={11} /> {t(m.label)}</span>;
}

/** Circular progress indicator, as in the prototype. */
export function Ring({ value, size = 44 }: { value: number; size?: number }) {
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <svg className="lrn-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle className="lrn-ring__bg" cx={size / 2} cy={size / 2} r={r} />
      <circle className="lrn-ring__fg" cx={size / 2} cy={size / 2} r={r}
        strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} />
      <text className="lrn-ring__txt" x="50%" y="50%">{pct}%</text>
    </svg>
  );
}

export function StatCard({ label, value, sub, tone = 'neutral', icon }: {
  label: string; value: ReactNode; sub?: string; tone?: string; icon: IconName;
}) {
  return (
    <div className="card lrn-stat">
      <div className={`lrn-stat__ico is-${tone}`}><Icon name={icon} size={17} /></div>
      <div>
        <div className="lrn-stat__label">{label}</div>
        <div className="lrn-stat__value">{value}</div>
        {sub && <div className="lrn-stat__sub">{sub}</div>}
      </div>
    </div>
  );
}

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="lrn-empty">
      <Icon name="cap" size={38} />
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
        {hint && <div style={{ fontSize: 13 }}>{hint}</div>}
      </div>
      {action}
    </div>
  );
}

export const fmtDue = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

export const isOverdue = (a: { dueAt: string | null; status: string } | null | undefined) =>
  !!a && a.status !== 'completed' && !!a.dueAt && new Date(a.dueAt).getTime() < Date.now();
